import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ResponsiveModalBottomSheet } from '@/components';
import Button from '@/components/Button';
import TextInput from '@/components/TextInput';
import type { CustomerPackage, SelectedPackage } from '@/models';
import { CashWalletBadge } from '@/pages/components/Badge';

interface CashWalletPackageBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  customerPackage: CustomerPackage | null;
  onConfirm: (selected: SelectedPackage) => Promise<void> | void;
  initialValue?: number;
}

const CashWalletPackageBottomSheet: React.FC<
  CashWalletPackageBottomSheetProps
> = ({ isOpen, onClose, customerPackage, onConfirm, initialValue }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(
    initialValue !== undefined ? initialValue.toString() : '',
  );
  const [loading, setLoading] = useState(false);

  // Sync when re-opening or when a different package/initialValue comes in
  useEffect(() => {
    if (isOpen) {
      setAmount(initialValue !== undefined ? initialValue.toString() : '');
    }
  }, [isOpen, initialValue, customerPackage?.id]);

  if (!customerPackage) return null;

  const remaining = customerPackage.remainingValue;
  const expiry = dayjs(customerPackage.expiryDate);
  const expiryLabel = expiry.format('DD/MM/YYYY');
  const remainsFraction = `${remaining.toLocaleString()}/${customerPackage.totalValue.toLocaleString()}`;

  const handleConfirm = async () => {
    const valueNum = parseFloat(amount || '0');
    if (valueNum <= 0 || valueNum > remaining) return;
    try {
      setLoading(true);
      // Here we would call validate API with new selected package list
      await onConfirm({ id: customerPackage.id, value: valueNum });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResponsiveModalBottomSheet
      isOpen={isOpen}
      onClose={onClose}
      stickyFooter={
        <div className="px-4 pt-2 pb-4">
          <div className="text-secondary-text mb-3 text-center text-sm leading-5">
            {t('package.cashwallet.maximum', {
              remaining: remaining.toLocaleString(),
            })}
          </div>
          <Button
            disabled={loading || !amount || parseFloat(amount) <= 0}
            onClick={handleConfirm}
          >
            {loading
              ? t('package.cashwallet.validating')
              : t('package.cashwallet.confirm')}
          </Button>
        </div>
      }
    >
      <div className="space-y-6 px-4 pt-2 pb-2">
        {/* Title (package name) */}
        <div className="flex flex-col gap-1">
          <h2 className="text-primary-text leading-6 font-medium">
            {customerPackage.name}
          </h2>
          <CashWalletBadge />
        </div>

        {/* Remains & Expiry */}
        <div className="border-divider grid grid-cols-2 gap-4 border-t border-b py-4 text-sm">
          <div className="flex flex-col gap-0.5">
            <span className="text-secondary-text text-sm leading-5">
              {t('package.cashwallet.remains')}
            </span>
            <span className="text-primary-text leading-6 font-medium">
              {remainsFraction}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-secondary-text text-sm leading-5">
              {t('package.cashwallet.expiry')}
            </span>
            <span className="text-primary-text leading-6">{expiryLabel}</span>
          </div>
        </div>

        {/* Enter amount */}
        <div className="space-y-3">
          <div className="text-secondary-text text-center leading-6 font-semibold">
            {t('package.cashwallet.enterAmount')}
          </div>
          <div className="flex items-center justify-center">
            <div className="relative h-9 w-[170px]">
              <TextInput
                size="small"
                placeholder={t('package.cashwallet.placeholder')}
                inputMode="numeric"
                value={amount}
                textAlign="center"
                className="font-semibold"
                style={{ fontSize: '16px' }}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9.]/g, '');

                  // Allow empty value
                  if (!v) {
                    setAmount('');
                    return;
                  }

                  // Only one decimal point, max 2 decimal digits
                  if (!/^\d*\.?\d{0,2}$/.test(v)) return;

                  const num = Number(v);

                  // Allow intermediate values like "12." & check agains remaining
                  if (v.endsWith('.') || num <= remaining) {
                    setAmount(v);
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </ResponsiveModalBottomSheet>
  );
};

export default CashWalletPackageBottomSheet;
